import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../stores/store';
import { useNavigate } from 'react-router-dom';
import {
    Chip, Button, CircularProgress, Card, CardContent,
    Typography, Alert, Tooltip, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField, IconButton, Box
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FeaturedPlayListIcon from '@mui/icons-material/FeaturedPlayList';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import SaveIcon from '@mui/icons-material/Save';
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import TuneIcon from "@mui/icons-material/Tune";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { useSetMenu } from "../../../hooks/useSetMenu";
import { useSnackbar } from 'notistack';
import CriterionValueGroup from '../../Generic/CriterionValueGroup';

const MyToolFeatures = () => {
    const axiosPrivate = useAxiosPrivate();
    const { mainStore } = useStore();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    useSetMenu(mainStore.MyToolFeaturesMenuType, mainStore.MyToolFeaturesMenuNo);

    const [loading, setLoading] = useState(true);
    const [toolFeatures, setToolFeatures] = useState([]);
    const [pendingRequests, setPendingRequests] = useState([]);

    // Delete dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingItem, setDeletingItem] = useState(null);
    const [deleteReason, setDeleteReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Edit dialog state
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingTool, setEditingTool] = useState(null);
    // Weight-grouped criteria state
    const [criteriaW1, setCriteriaW1] = useState([]);
    const [criteriaW05, setCriteriaW05] = useState([]);
    const [criteriaW033, setCriteriaW033] = useState([]);
    const [selectedCVIdsW1, setSelectedCVIdsW1] = useState([]);
    const [selectedCVIdsW05, setSelectedCVIdsW05] = useState([]);
    const [selectedCVIdsW033, setSelectedCVIdsW033] = useState([]);
    const [loadingCriteria, setLoadingCriteria] = useState(false);

    // Fetch user's approved tool features
    const fetchToolFeatures = useCallback(async () => {
        try {
            const response = await axiosPrivate.get('/ToolFeatures/MyToolFeatures');
            setToolFeatures(response.data);
        } catch (error) {
            console.error('Error fetching tool features:', error);
            enqueueSnackbar(t('errorMessageError'), { variant: 'error' });
        }
    }, [axiosPrivate, enqueueSnackbar, t]);

    // Fetch pending requests
    const fetchPendingRequests = useCallback(async () => {
        try {
            const response = await axiosPrivate.get('/ToolFeatures/MyToolFeatures/AllRequests');
            const pendingOnly = response.data.filter(r => r.status === 'Pending');
            setPendingRequests(pendingOnly);
        } catch (error) {
            console.error('Error fetching pending requests:', error);
        }
    }, [axiosPrivate]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            await Promise.all([fetchToolFeatures(), fetchPendingRequests()]);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, [fetchToolFeatures, fetchPendingRequests]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Group tool features by tool passport
    const groupedByTool = useMemo(() => {
        const grouped = {};
        toolFeatures.forEach(tf => {
            const key = `${tf.toolPassportId}-${tf.subcategoryId}`;
            if (!grouped[key]) {
                grouped[key] = {
                    toolPassportId: tf.toolPassportId,
                    toolPassportName: tf.toolPassportName,
                    subcategoryId: tf.subcategoryId,
                    subcategoryName: tf.subcategoryName,
                    categoryId: tf.categoryId,
                    categoryName: tf.categoryName,
                    panelId: tf.panelId,
                    panelName: tf.panelName,
                    features: []
                };
            }
            grouped[key].features.push(tf);
        });
        return Object.values(grouped);
    }, [toolFeatures]);

    // Check if a tool feature has a pending request
    const hasPendingRequest = useCallback((toolFeatureId) => {
        return pendingRequests.some(r => r.originalToolFeatureId === toolFeatureId);
    }, [pendingRequests]);

    // Check if a tool group has any pending delete requests
    const hasAnyPendingDeleteRequest = useCallback((toolGroup) => {
        return toolGroup.features.some(f =>
            pendingRequests.some(r => r.originalToolFeatureId === f.id && r.requestType === 'Delete')
        );
    }, [pendingRequests]);

    // Count pending delete requests for a tool group
    const countPendingDeleteRequests = useCallback((toolGroup) => {
        return toolGroup.features.filter(f =>
            pendingRequests.some(r => r.originalToolFeatureId === f.id && r.requestType === 'Delete')
        ).length;
    }, [pendingRequests]);

    // Handle delete click for entire tool group (bulk delete)
    const handleDeleteClick = (toolGroup) => {
        const pendingCount = countPendingDeleteRequests(toolGroup);
        if (pendingCount === toolGroup.features.length) {
            enqueueSnackbar(t('allFeaturesHavePendingDeleteRequests'), { variant: 'warning' });
            return;
        }
        setDeletingItem(toolGroup);
        setDeleteReason('');
        setDeleteDialogOpen(true);
    };

    // Submit bulk delete request
    const handleDeleteSubmit = async () => {
        if (!deletingItem) return;

        setSubmitting(true);
        try {
            await axiosPrivate.post('/ToolFeatures/MyToolFeatures/RequestBulkDelete', {
                toolPassportId: deletingItem.toolPassportId,
                subcategoryId: deletingItem.subcategoryId,
                reason: deleteReason
            });
            enqueueSnackbar(t('requestDeleteSuccess'), { variant: 'success' });
            setDeleteDialogOpen(false);
            setDeletingItem(null);
            setDeleteReason('');
            fetchData();
        } catch (error) {
            console.error('Error submitting delete request:', error);
            enqueueSnackbar(t('requestDeleteError'), { variant: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    // Handle edit click - opens dialog to select/deselect criteria values
    const handleEditClick = async (toolGroup) => {
        setEditingTool(toolGroup);
        setLoadingCriteria(true);
        setEditDialogOpen(true);

        try {
            // Load criteria with values for this subcategory (public endpoint)
            const response = await axiosPrivate.get(`/Criterias/Public/WithValuesBySubcategory/${toolGroup.subcategoryId}`);
            const data = response.data;
            let allCriteria = [];
            if (typeof data === "string" && data === "No records") {
                allCriteria = [];
            } else if (!Array.isArray(data)) {
                allCriteria = [data];
            } else {
                allCriteria = data;
            }

            // Group criteria by weight coefficient
            setCriteriaW1(allCriteria.filter((c) => c.weightCoefficient === 1.0));
            setCriteriaW05(allCriteria.filter((c) => c.weightCoefficient === 0.5));
            setCriteriaW033(allCriteria.filter((c) => c.weightCoefficient === 0.33));

            // Set currently selected criteria value IDs distributed by weight group
            const currentIds = toolGroup.features.map(f => f.criteriaValueId);

            // Build maps to know which criteria value belongs to which weight group
            const w1ValueIds = new Set();
            const w05ValueIds = new Set();
            const w033ValueIds = new Set();

            allCriteria.forEach(crit => {
                crit.values.forEach(val => {
                    if (crit.weightCoefficient === 1.0) w1ValueIds.add(val.id);
                    else if (crit.weightCoefficient === 0.5) w05ValueIds.add(val.id);
                    else if (crit.weightCoefficient === 0.33) w033ValueIds.add(val.id);
                });
            });

            setSelectedCVIdsW1(currentIds.filter(id => w1ValueIds.has(id)));
            setSelectedCVIdsW05(currentIds.filter(id => w05ValueIds.has(id)));
            setSelectedCVIdsW033(currentIds.filter(id => w033ValueIds.has(id)));
        } catch (error) {
            console.error('Error loading criteria:', error);
            enqueueSnackbar(t('errorLoadingCriteria'), { variant: 'error' });
        } finally {
            setLoadingCriteria(false);
        }
    };

    // Get all selected criteria value IDs combined from all weight groups
    const allSelectedCriteriaValueIds = [...selectedCVIdsW1, ...selectedCVIdsW05, ...selectedCVIdsW033];

    // Submit edit request (creates new features for added values, delete requests for removed values)
    const handleEditSubmit = async () => {
        if (!editingTool) return;

        const currentIds = editingTool.features.map(f => f.criteriaValueId);
        const addedIds = allSelectedCriteriaValueIds.filter(id => !currentIds.includes(id));
        const removedIds = currentIds.filter(id => !allSelectedCriteriaValueIds.includes(id));

        if (addedIds.length === 0 && removedIds.length === 0) {
            enqueueSnackbar(t('noChangesToSave'), { variant: 'info' });
            return;
        }

        setSubmitting(true);
        try {
            // Submit new features for added criteria values
            if (addedIds.length > 0) {
                await axiosPrivate.post('/ToolFeatures/Submit', {
                    toolPassportId: editingTool.toolPassportId,
                    criteriaValueIds: addedIds,
                    subcategoryId: editingTool.subcategoryId,
                    categoryId: editingTool.categoryId,
                    panelId: editingTool.panelId
                });
            }

            // Request deletion for removed features
            for (const removedId of removedIds) {
                const featureToRemove = editingTool.features.find(f => f.criteriaValueId === removedId);
                if (featureToRemove && !hasPendingRequest(featureToRemove.id)) {
                    await axiosPrivate.post(`/ToolFeatures/MyToolFeatures/${featureToRemove.id}/RequestDelete`, {
                        reason: t('removedDuringEdit')
                    });
                }
            }

            enqueueSnackbar(t('editMessageSuccess'), { variant: 'success' });
            setEditDialogOpen(false);
            setEditingTool(null);
            // Clear criteria groups
            setCriteriaW1([]);
            setCriteriaW05([]);
            setCriteriaW033([]);
            setSelectedCVIdsW1([]);
            setSelectedCVIdsW05([]);
            setSelectedCVIdsW033([]);
            fetchData();
        } catch (error) {
            console.error('Error submitting edit:', error);
            enqueueSnackbar(t('editMessageError'), { variant: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    // Calculate summary stats
    const totalToolFeatures = toolFeatures.length;
    const totalTools = groupedByTool.length;
    const pendingDeleteRequests = pendingRequests.filter(r => r.requestType === 'Delete').length;

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">
                    <FeaturedPlayListIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    {t("myToolFeaturesTitle")}
                </h4>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/SubmitToolFeature')}
                    size="small"
                >
                    {t("submitNewToolFeature")}
                </Button>
            </div>

            <Alert severity="info" className="mb-3">
                {t("myToolFeaturesInfo")}
            </Alert>

            {/* Summary Cards */}
            <div className="row mb-4">
                <div className="col-md-3 col-sm-6 mb-2">
                    <Card>
                        <CardContent sx={{ py: 2 }}>
                            <Typography variant="h4" component="div" color="primary.main">
                                {totalTools}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('tools')}
                            </Typography>
                        </CardContent>
                    </Card>
                </div>
                <div className="col-md-3 col-sm-6 mb-2">
                    <Card>
                        <CardContent sx={{ py: 2 }}>
                            <Typography variant="h4" component="div" color="secondary.main">
                                {totalToolFeatures}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('totalToolFeatures')}
                            </Typography>
                        </CardContent>
                    </Card>
                </div>
                <div className="col-md-3 col-sm-6 mb-2">
                    <Card sx={{ borderLeft: 4, borderColor: 'error.main' }}>
                        <CardContent sx={{ py: 2 }}>
                            <Typography variant="h4" component="div" color="error.main">
                                {pendingDeleteRequests}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('pendingDeleteRequests')}
                            </Typography>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Tool Features Grouped by Tool */}
            {loading ? (
                <div className="d-flex justify-content-center p-5">
                    <CircularProgress />
                </div>
            ) : groupedByTool.length === 0 ? (
                <div className="text-center p-5">
                    <FeaturedPlayListIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">
                        {t('noToolFeaturesYet')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" className="mb-3">
                        {t('noToolFeaturesDescription')}
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/SubmitToolFeature')}
                    >
                        {t("submitNewToolFeature")}
                    </Button>
                </div>
            ) : (
                <div>
                    {groupedByTool.map((toolGroup) => (
                        <Card
                            key={`${toolGroup.toolPassportId}-${toolGroup.subcategoryId}`}
                            sx={{ mb: 2 }}
                        >
                            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <BuildIcon color="primary" />
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                {toolGroup.toolPassportName}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {toolGroup.subcategoryName} • {toolGroup.categoryName} • {toolGroup.panelName}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Chip
                                            size="small"
                                            label={`${toolGroup.features.length} ${t('features')}`}
                                            color="primary"
                                            variant="outlined"
                                        />
                                        {hasAnyPendingDeleteRequest(toolGroup) && (
                                            <Chip
                                                size="small"
                                                label={`${countPendingDeleteRequests(toolGroup)} ${t('pendingDelete')}`}
                                                color="warning"
                                                variant="filled"
                                            />
                                        )}
                                        <Tooltip title={t('edit')}>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => handleEditClick(toolGroup)}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('requestDelete')}>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeleteClick(toolGroup)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DeleteIcon color="error" />
                    {t('confirmDeleteRequest')}
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" className="mb-3">
                        {t('confirmDeleteAllToolFeaturesMessage')}
                    </Alert>

                    {deletingItem && (
                        <Box sx={{ mb: 3, p: 2, bgcolor: '#ffebee', borderRadius: 1, border: '1px solid #ef5350' }}>
                            <Typography variant="subtitle2" color="error.main" gutterBottom>
                                {t('toolFeaturesToBeDeleted')}:
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                <strong>{t('toolPassport')}:</strong> {deletingItem.toolPassportName}
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                <strong>{t('subcategory')}:</strong> {deletingItem.subcategoryName}
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                <strong>{t('totalFeatures')}:</strong> {deletingItem.features?.length || 0}
                            </Typography>
                            {countPendingDeleteRequests(deletingItem) > 0 && (
                                <Alert severity="info" sx={{ mt: 1 }}>
                                    {t('someFeaturesPendingNote', { count: countPendingDeleteRequests(deletingItem) })}
                                </Alert>
                            )}
                        </Box>
                    )}

                    <Alert severity="info" className="mb-3">
                        {t('requestDeleteInfo')}
                    </Alert>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label={t('deleteReason')}
                        placeholder={t('deleteReasonPlaceholder')}
                        value={deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                        variant="outlined"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={submitting}>
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={handleDeleteSubmit}
                        color="error"
                        variant="contained"
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
                    >
                        {t('requestDelete')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PlaylistAddCheckIcon color="primary" />
                        {t('selectCriteriaValues')}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {editingTool && (
                        <Box sx={{ mb: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {t('toolPassport')}:
                            </Typography>
                            <Typography variant="h6">
                                {editingTool.toolPassportName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {editingTool.subcategoryName} • {editingTool.categoryName}
                            </Typography>
                        </Box>
                    )}

                    <Alert severity="info" className="mb-3">
                        {t('requestEditInfo')}
                    </Alert>

                    {loadingCriteria ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (criteriaW1.length > 0 || criteriaW05.length > 0 || criteriaW033.length > 0) ? (
                        <div>
                            {/* Mandatory Criteria (weight = 1.00) */}
                            <div style={{ marginBottom: "1.5rem" }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: '12px 16px',
                                    background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                                    borderRadius: '8px 8px 0 0',
                                    marginBottom: '12px'
                                }}>
                                    <PriorityHighIcon sx={{ color: '#fff', fontSize: 22 }} />
                                    <div>
                                        <h4 style={{ margin: 0, color: '#fff', fontSize: '1em', fontWeight: '600' }}>
                                            {t("Mandatory Criteria")} (W = 1.00)
                                        </h4>
                                        <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.75em' }}>
                                            {t("Highest priority criteria")}
                                        </span>
                                    </div>
                                    <span style={{
                                        marginLeft: 'auto',
                                        background: 'rgba(255,255,255,0.2)',
                                        color: '#fff',
                                        padding: '4px 10px',
                                        borderRadius: '12px',
                                        fontSize: '0.75em',
                                        fontWeight: '600'
                                    }}>
                                        {selectedCVIdsW1.length} / {criteriaW1.reduce((acc, c) => acc + c.values.length, 0)} {t("selected")}
                                    </span>
                                </div>
                                {criteriaW1.length === 0 ? (
                                    <div style={{
                                        padding: '20px',
                                        textAlign: 'center',
                                        color: '#94a3b8',
                                        background: '#f8fafc',
                                        borderRadius: '0 0 8px 8px',
                                        border: '1px dashed #e2e8f0'
                                    }}>
                                        {t("No mandatory criteria available")}
                                    </div>
                                ) : (
                                    criteriaW1.map((crit) => (
                                        <CriterionValueGroup
                                            key={crit.id}
                                            criterion={crit}
                                            selectedValueIds={selectedCVIdsW1}
                                            setSelectedValueIds={setSelectedCVIdsW1}
                                        />
                                    ))
                                )}
                            </div>

                            {/* Criteria (weight = 0.50) */}
                            <div style={{ marginBottom: "1.5rem" }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: '12px 16px',
                                    background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
                                    borderRadius: '8px 8px 0 0',
                                    marginBottom: '12px'
                                }}>
                                    <TuneIcon sx={{ color: '#fff', fontSize: 22 }} />
                                    <div>
                                        <h4 style={{ margin: 0, color: '#fff', fontSize: '1em', fontWeight: '600' }}>
                                            {t("Criteria")} (W = 0.50)
                                        </h4>
                                        <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.75em' }}>
                                            {t("Medium priority criteria")}
                                        </span>
                                    </div>
                                    <span style={{
                                        marginLeft: 'auto',
                                        background: 'rgba(255,255,255,0.2)',
                                        color: '#fff',
                                        padding: '4px 10px',
                                        borderRadius: '12px',
                                        fontSize: '0.75em',
                                        fontWeight: '600'
                                    }}>
                                        {selectedCVIdsW05.length} / {criteriaW05.reduce((acc, c) => acc + c.values.length, 0)} {t("selected")}
                                    </span>
                                </div>
                                {criteriaW05.length === 0 ? (
                                    <div style={{
                                        padding: '20px',
                                        textAlign: 'center',
                                        color: '#94a3b8',
                                        background: '#f8fafc',
                                        borderRadius: '0 0 8px 8px',
                                        border: '1px dashed #e2e8f0'
                                    }}>
                                        {t("No criteria with weight 0.50 available")}
                                    </div>
                                ) : (
                                    criteriaW05.map((crit) => (
                                        <CriterionValueGroup
                                            key={crit.id}
                                            criterion={crit}
                                            selectedValueIds={selectedCVIdsW05}
                                            setSelectedValueIds={setSelectedCVIdsW05}
                                        />
                                    ))
                                )}
                            </div>

                            {/* Criteria (weight = 0.33) */}
                            <div style={{ marginBottom: "1.5rem" }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: '12px 16px',
                                    background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                                    borderRadius: '8px 8px 0 0',
                                    marginBottom: '12px'
                                }}>
                                    <AddCircleOutlineIcon sx={{ color: '#fff', fontSize: 22 }} />
                                    <div>
                                        <h4 style={{ margin: 0, color: '#fff', fontSize: '1em', fontWeight: '600' }}>
                                            {t("Criteria")} (W = 0.33)
                                        </h4>
                                        <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.75em' }}>
                                            {t("Lower priority criteria")}
                                        </span>
                                    </div>
                                    <span style={{
                                        marginLeft: 'auto',
                                        background: 'rgba(255,255,255,0.2)',
                                        color: '#fff',
                                        padding: '4px 10px',
                                        borderRadius: '12px',
                                        fontSize: '0.75em',
                                        fontWeight: '600'
                                    }}>
                                        {selectedCVIdsW033.length} / {criteriaW033.reduce((acc, c) => acc + c.values.length, 0)} {t("selected")}
                                    </span>
                                </div>
                                {criteriaW033.length === 0 ? (
                                    <div style={{
                                        padding: '20px',
                                        textAlign: 'center',
                                        color: '#94a3b8',
                                        background: '#f8fafc',
                                        borderRadius: '0 0 8px 8px',
                                        border: '1px dashed #e2e8f0'
                                    }}>
                                        {t("No criteria with weight 0.33 available")}
                                    </div>
                                ) : (
                                    criteriaW033.map((crit) => (
                                        <CriterionValueGroup
                                            key={crit.id}
                                            criterion={crit}
                                            selectedValueIds={selectedCVIdsW033}
                                            setSelectedValueIds={setSelectedCVIdsW033}
                                        />
                                    ))
                                )}
                            </div>
                        </div>
                    ) : (
                        <Alert severity="warning">
                            {t('noCriteriaAvailable')}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)} disabled={submitting}>
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={handleEditSubmit}
                        color="primary"
                        variant="contained"
                        disabled={submitting || allSelectedCriteriaValueIds.length === 0}
                        startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                        {t('save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default observer(MyToolFeatures);
